/**
 * RoleTask — the PROPOSED half of a Role's register.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS, AND WHY IT IS NOT A SECOND MAP
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A "task" in this product is already a row in a Role's register: a
 * `RoleResource` card on the Role's map, priced by the `RoleSystemImpact`
 * keyed 1:1 on it. That pair ships, with a full write path
 * (`POST /api/roles/:id/resources`, `PUT /api/roles/:id/impacts/:roleResourceId`),
 * and NOTHING here duplicates it. The coverage engine's numerator is the sum of
 * those impacts and its denominator is `RoleWorkload`; a row of this table is in
 * neither, on purpose.
 *
 * What did not exist is the state BEFORE a system backs the work. Every
 * `RoleResource.resourceId` must name a row in one of the real tables
 * `RoleResourceType` lists, so a task that a person can describe but nobody has
 * built yet had nowhere to live. `UnbackedLedger.tsx` rendered that gap as a
 * product boundary, in the product's own words — and that line is GONE from the
 * card, deleted rather than softened, because this table closed the gap it
 * named. **Do not re-derive the absence from that component: it is now the
 * evidence that the state exists.**
 *
 * This table IS that state, and nothing more. A RoleTask is work the Role does,
 * named and costed by a human, with no system doing it yet. It graduates by
 * becoming a `RoleResource` + `RoleSystemImpact` pair; until then it is a
 * proposal, and it is deliberately invisible to the automation percentage —
 * a proposed automation that counted as automation would overstate every
 * customer's coverage on the one screen where a wrong number is worse than an
 * error.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY A NEW TABLE RATHER THAN TWO COLUMNS ON `RoleSystemImpact`
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Bolting `name` and `description` onto `RoleSystemImpact` is the cheaper edit
 * and it does not work, for two independent reasons.
 *
 * `RoleSystemImpact` is keyed on `roleResourceId`, so authoring one still
 * requires a `RoleResource`, which still requires a real agent or workflow to
 * point at. The cheap edit leaves the actual gap exactly where it was.
 *
 * And it would put the wizard in the conflict `newRoleDraft.ts` already refuses
 * by name: the Role's General tabs rewrite the coverage documents WHOLE, so a
 * wizard step writing into them would be a SECOND author on last-writer-wins.
 * A table whose only author is this step has no such problem. That is not a
 * side benefit; it is why the shape is a separate table.
 */

import { z } from "zod";

import { RoleResourceTypeSchema } from "./role.types";

/**
 * Money a task brings in, per year, in the organisation's own currency.
 *
 * ⚠️ ONE DEFINITION ON PURPOSE. Every money field in this contract reads this
 * schema, so the numeric decision is made in one place and cannot drift between
 * the request, the view and the mapper.
 *
 * Bounded rather than open. `z.number()` alone admits `Infinity` and `NaN`
 * (both are JSON-expressible through a client that stringifies them itself),
 * and an unbounded magnitude reaches a customer-facing ROI total where one
 * mistyped figure silently dominates the sum. The cap is far above any real
 * annual figure and far below the point at which a double stops representing
 * whole cents exactly.
 *
 * NEGATIVE IS REFUSED, and that is a product statement rather than a numeric
 * one: this column is revenue. Money a task COSTS is a different term with a
 * different sign convention, and `RoleSystemImpact` already keeps revenue and
 * cost in two columns precisely because a net of zero from 0/0 and from 400/400
 * are different facts. Allowing a negative here would quietly invent a third
 * spelling of cost.
 */
export const RoleTaskMoneySchema = z
  .number()
  .refine((n) => Number.isFinite(n), { message: "must be a finite amount" })
  .min(0, { message: "revenue cannot be negative" })
  .max(1_000_000_000_000, { message: "revenue is implausibly large" });

/**
 * A count stated per year — occurrences of the task, or people it takes.
 *
 * `>= 0` and finite, restating the CHECK constraints on the columns. Float and
 * not Int deliberately: half a person a year and a task that happens every
 * other year are both ordinary answers, and the sibling quantity in this model
 * (`RoleScopeLine.quantity`) is Float for the same reason.
 */
export const RoleTaskPerYearSchema = z
  .number()
  .refine((n) => Number.isFinite(n), { message: "must be a finite number" })
  .min(0, { message: "cannot be negative" })
  .max(1_000_000_000, { message: "implausibly large" });

/**
 * Who or what does a task — the `+ Add people or a tool` control.
 *
 * EXACTLY ONE ARM, and it is a database CHECK rather than an application rule.
 * A row carrying both a person and a tool has no meaning and a row carrying
 * neither is not an assignment; both are unrepresentable rather than merely
 * refused here.
 *
 * The tool arm is `(resourceType, resourceId)` and NOT a foreign key, matching
 * `RoleResource` exactly. That column's own docstring records why a database
 * enum is wrong for it: `collection` was a `RoleResourceType` until its rows
 * moved to `RoleCollectionGrant`, and the old rows did not disappear when the
 * union narrowed. Postgres cannot remove a value from an enum.
 *
 * ⚠️ THIS SCHEMA DOES NOT ESTABLISH TENANCY AND MUST NEVER BE READ AS DOING SO.
 * A shape check says an id is a uuid; it says nothing about whose. Every id here
 * is verified to belong to the caller's organisation in the use case, before a
 * row is written — see `ReplaceRoleTasksUseCase`. A schema that looks this
 * strict is exactly the kind that persuades a reviewer the check already
 * happened.
 */
const roleTaskAssignmentArms = [
  z.strictObject({
    kind: z.literal("person"),
    /** A user of the caller's organisation. Membership is verified in the use case. */
    userId: z.string().min(1).max(255)
  }),
  z.strictObject({
    kind: z.literal("resource"),
    /**
     * Which member kind. READ OUT OF `RoleResourceType`, never restated: this
     * list was written out by hand and went stale the day `external_tool` moved
     * to `RoleExternalToolGrant`, so the schema accepted a kind the use case
     * then refused as "not this organisation's" — a wrong sentence about the
     * caller's data for a value the contract itself had retired.
     */
    resourceType: RoleResourceTypeSchema,
    /**
     * Uuid-checked on the way IN for the reason `RoleResourceIdSchema` gives:
     * every one of those tables keys on `@db.Uuid`, so a malformed id cannot be
     * compared to the column at all and reaches Prisma as a `P2023`, which
     * surfaces as a 500 telling the caller the server broke when their input was
     * invalid.
     */
    resourceId: z.string().uuid()
  })
] as const;

/**
 * The arms above, named the way zod names an enum's members.
 *
 * 🚨 READ OFF THE ARMS, NEVER TYPED A SECOND TIME. A message listing the arms
 * by hand is the next thing to drift — which is the whole defect this schema
 * already had one layer up, in the CLI's `--help`.
 */
const ROLE_TASK_ASSIGNMENT_ARM_NAMES = roleTaskAssignmentArms
  .flatMap((arm) => [...arm.shape.kind.values])
  .map((value) => JSON.stringify(value))
  .join("|");

/**
 * 🚨 DISCRIMINATED, AND WITH AN `error` THAT NAMES THE ARMS — THE SECOND HALF IS
 * NOT DECORATION, IT IS THE FIX.
 *
 * A plain `z.union` reports a bare `Invalid input` at the ASSIGNMENT, because
 * `formatZodError` publishes each top-level issue's `path` and `message` and
 * nothing else — the per-branch errors a union nests inside `issues[0].errors`
 * never reach the wire. `nexus role set-tasks` therefore answered
 * `tasks.0.assignments.0: Invalid input` for every malformed arm, while the
 * `basis` and `group` enums one field away enumerated their values.
 *
 * ⚠️ `z.discriminatedUnion` ALONE DOES NOT FIX THAT ON ZOD 4. Measured against
 * zod 4.1.13: an unmatched discriminator yields
 * `{ code: "invalid_union", note: "No matching discriminator", message:
 * "Invalid input" }` — a better PATH (`…assignments.0.kind`) and the same
 * uninformative message, with the allowed values absent from the issue
 * altogether. Zod 3's `invalid_union_discriminator`, which carried `options`, is
 * gone. The enumeration has to be supplied, and the only honest way to supply it
 * is to read it back out of the arms.
 *
 * The wording deliberately copies zod's own `z.enum` message, so a caller who
 * has already met `basis` or `group` reads one sentence, not two.
 */
export const RoleTaskAssignmentInputSchema = z.discriminatedUnion("kind", roleTaskAssignmentArms, {
  error: () => `Invalid option: expected one of ${ROLE_TASK_ASSIGNMENT_ARM_NAMES}`
});

export type RoleTaskAssignmentInput = z.infer<typeof RoleTaskAssignmentInputSchema>;

/**
 * One task of a Role, as a caller authors it.
 *
 * NO `id` AND NO `position`. The whole list is replaced in one request, so a
 * task's identity is its index in the array — the same contract
 * `RoleScopeLinesInputSchema` and `RoleVariablesInputSchema` already make, for
 * the same reasons.
 */
export const RoleTaskInputSchema = z.strictObject({
  /**
   * WHICH STORED ROW THIS IS, when it is one that already exists.
   *
   * 🚨 THE LINKS ARE WHY THIS FIELD EXISTS. `RoleTaskResponsibility.taskId`
   * carries `onDelete: Cascade`, so a task that comes back with a new `id` takes
   * every responsibility link on the Role with it — silently, with a 200, and with no
   * statement anywhere near the link table. Naming the row is what lets the
   * replace update it in place instead of re-minting it.
   *
   * ABSENT MEANS NEW. A body that omits it asks for a row to be created, which
   * is what every task in a first save does. It is `.optional()` rather than
   * nullable for that reason: "not stated" and "no id" are the same request, and
   * a second spelling of it would be a shape a client could get wrong.
   *
   * 🚨 THE SHAPE IS ALL THIS PROVES. Whether the id names a task of THIS Role is
   * a question only a query answers, and `ReplaceRoleTasksUseCase` answers it
   * before any row is written — the same split `RoleTaskAssignmentInputSchema`
   * states for its own two arms. A schema this strict is exactly the kind that
   * persuades a reviewer the ownership check already happened.
   */
  id: z.string().uuid().optional(),
  /**
   * What the work is called. Trimmed and required — a task with no name is a
   * row nobody can act on, and the register renders the name as its identity.
   * Restates `RoleTask_name_shape`.
   */
  name: z.string().trim().min(1).max(160),
  /**
   * The one line under the name.
   *
   * 🚨 `null` IS "NOBODY WROTE ONE" AND IS NOT THE EMPTY STRING. The register
   * renders an absent description as nothing at all and a written-then-cleared
   * one identically, but the two are different authoring states and the column
   * keeps them apart. Collapsing them here would make the distinction
   * unrecoverable at the boundary rather than merely unrendered.
   */
  description: z.string().max(500).nullable(),
  /**
   * How many times the work happens a year.
   *
   * 🚨 `null` IS "NOT STATED" AND IS NOT ZERO — the distinction
   * `RoleVariable.value` already draws and for the same reason. A task nobody
   * has quantified must read as unfilled; a defaulted `0` prices unfinished
   * work as free on the screen whose entire subject is what the work is worth.
   */
  occurrencesPerYear: RoleTaskPerYearSchema.nullable(),
  /** How many people it takes a year. `null` is "not stated". See above. */
  peoplePerYear: RoleTaskPerYearSchema.nullable(),
  /** What a year of it brings in. `null` is "not stated", never zero. */
  revenuePerYear: RoleTaskMoneySchema.nullable(),
  /**
   * Who and what does it. An empty array is the ordinary state — a proposed
   * task usually has nobody assigned yet, which is the whole point of it being
   * a proposal.
   */
  assignments: z.array(RoleTaskAssignmentInputSchema).max(50)
});

export type RoleTaskInput = z.infer<typeof RoleTaskInputSchema>;

/**
 * A Role's whole task list, in order.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REPLACE-THE-LIST, AND THE HAZARD THAT COMES WITH IT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The array's INDEX is the position. One request states the whole list, one
 * transaction replaces it, and a reorder is the same request with the elements
 * moved — no `.../order` literal route, which is also what keeps this resource
 * out of the `@Get(":id")` catch-all race that has already killed two Roles
 * endpoints.
 *
 * ⚠️ A ONE-ELEMENT PUT DELETES EVERY OTHER TASK AND RETURNS 200. That is the
 * contract, not a defect, and it is the same shape `ReplaceRoleScopeLines` and
 * `ReplaceRoleVariables` carry — but it is the single most expensive thing to
 * get wrong about this endpoint, because success and destruction are the same
 * response. A caller that holds one task must send the others alongside it.
 *
 * ⚠️ A TASK'S `id` SURVIVES A SAVE. A task submitted WITH its `id` is updated in
 * place and keeps it; one submitted without an `id` is created; one the body
 * omits is deleted. `RoleTaskResponsibility` references a task by id and relies
 * on this — a re-minted id would cascade every responsibility link on the Role away.
 *
 * ⚠️ AN ASSIGNMENT'S `id` STILL DOES NOT SURVIVE. Assignments are deleted and
 * re-inserted under their task on every save, because nothing references one by
 * id. Do not build anything that assumes otherwise without changing this
 * contract first.
 */
export const RoleTasksInputSchema = z.strictObject({
  tasks: z.array(RoleTaskInputSchema).max(100)
});

export type RoleTasksInput = z.infer<typeof RoleTasksInputSchema>;

/**
 * One assignment, as the API returns it.
 *
 * ⚠️ IT CARRIES IDS AND NO DISPLAY NAME, DELIBERATELY, AND THE TWO ARMS REACH
 * THAT ANSWER DIFFERENTLY.
 *
 * The TOOL arm has no foreign key to join through — `resourceType` is loose TEXT
 * spanning every table `RoleResourceType` lists, so a name would cost one query
 * per kind on every read. The PERSON arm could be joined (`userId` does key into
 * `User`), but resolving only half the list server-side would hand the client two
 * lookup strategies for one control, and the client already has the cheaper one
 * for both.
 *
 * Every screen that renders a task holds the lookup already: the wizard has the
 * organisation's people from `useOrgPeople`, and the Role detail page has the
 * Role's resources from its own map read.
 *
 * So the client resolves an id it cannot find to a neutral placeholder. Note
 * that a DELETED USER is not one of those cases — the person arm cascades on
 * `User`, so the assignment row goes with the person rather than dangling.
 */
export const RoleTaskAssignmentViewSchema = z.union([
  z.strictObject({
    kind: z.literal("person"),
    userId: z.string()
  }),
  z.strictObject({
    kind: z.literal("resource"),
    /**
     * Deliberately NOT uuid-checked on the way out, and deliberately a loose
     * string for the type. Reads stay permissive for the reason
     * `RoleResourceRefSchema` gives: tightening a RESPONSE turns one legacy row
     * into a failed listing for the whole Role.
     */
    resourceType: z.string(),
    resourceId: z.string()
  })
]);

/** One stored task, as the API returns it. */
export const RoleTaskViewSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  occurrencesPerYear: z.number().nullable(),
  peoplePerYear: z.number().nullable(),
  revenuePerYear: z.number().nullable(),
  /** Its place in the list, `0`-based. The read is ordered by it. */
  position: z.number(),
  assignments: z.array(RoleTaskAssignmentViewSchema),
  updatedAt: z.string()
});

export type RoleTaskView = z.infer<typeof RoleTaskViewSchema>;

/** A Role's whole task list, in read order. */
export const RoleTasksViewSchema = z.strictObject({
  tasks: z.array(RoleTaskViewSchema)
});

export type RoleTasksView = z.infer<typeof RoleTasksViewSchema>;
